import { environment, EnvironmentLocalesEnum } from '@shared/environments';
import { isArray, merge } from 'lodash-es';

import { USER_DATA_SCHEMA_VERSION } from './user-data.store';
import { resetUserData } from './user-data.validators';

export const upgradeUserData = (data: any): any => {
  let upgradedData = structuredClone(data);

  if (isArray(upgradedData.appData)) {
    upgradedData.appData = {};
  }

  let dataVersion =
    upgradedData.appData.version || upgradedData.appData.schemaVersion;

  if (dataVersion) {
    let breakCounter = 0;

    while (
      dataVersion &&
      dataVersion < USER_DATA_SCHEMA_VERSION &&
      breakCounter < 500
    ) {
      try {
        const currentUpgradedData: any | undefined =
          migrations[dataVersion]?.(upgradedData);

        if (currentUpgradedData) {
          upgradedData = currentUpgradedData;
          dataVersion =
            upgradedData.appData.version || upgradedData.appData.schemaVersion;
        } else {
          break;
        }

        breakCounter++;
      } catch (error) {
        break;
      }
    }

    if (dataVersion && dataVersion === USER_DATA_SCHEMA_VERSION) {
      return upgradedData;
    } else {
      console.error('Something went wrong during data upgrade:', upgradedData);
      return resetUserData(data);
    }
  } else {
    return upgradedData;
  }
};

export const migrations: Record<number, (data: any) => any> = {
  3: (data) => {
    data.appData.version = 6;
    return data;
  },
  4: (data: any) => {
    data.appData.version = 6;
    return data;
  },
  5: (data: any) => {
    if (data?.appData?.table?.['sites']?.columns?.entities['connections']) {
      data.appData.table['sites'].columns.entities['connections'].minWidth =
        260;
    }

    data.appData.version = 6;

    return data;
  },
  6: (data: any) => {
    Object.keys(data?.appData?.table ?? []).forEach((key) => {
      delete data?.appData?.table?.[key]?.columns?.entities?.actions;
      delete data?.appData?.table?.[key]?.columns?.entities?.placeholder;

      if (data?.appData?.table?.[key]?.columns?.ids) {
        data.appData.table[key].columns.ids = data?.appData?.table[
          key
        ]?.columns?.ids.filter((item: string | number) => {
          return item !== 'actions' && item !== 'placeholder';
        });
      }
    });

    data.appData.version = 7;

    return data;
  },
  7: (data: any) => {
    if (data?.appData?.table?.['event-log-hosts']) {
      delete data.appData.table['event-log-hosts'];
    }

    data.appData.version = 8;

    return data;
  },
  8: (data: any) => {
    const updatedTables = [
      'event-log-all',
      'event-log-sites',
      'event-log-devices',
      'event-log-hosts',
      'event-log-other',
    ];
    const newColumnOrder = [
      'index',
      'operation',
      'event',
      'date',
      'site',
      'device',
      'user',
      'status',
    ];
    updatedTables.forEach((key: string) => {
      if (data?.appData?.table?.[key]?.columns?.ids) {
        data.appData.table[key].columns.ids = newColumnOrder.filter((colId) =>
          data.appData.table?.[key].columns.ids.includes(colId),
        );
        const entities = data.appData.table[key].columns.entities;

        if (entities?.['operation']) {
          entities['operation'] = {
            ...entities['operation'],
            headerComponentParams: {
              enableMoving: false,
              enablePinning: true,
            },
            lockPosition: 'left',
          };
        }

        if (entities?.['event']) {
          entities['event'] = {
            ...entities['event'],
            headerComponentParams: {
              enableMoving: false,
              enableSorting: false,
            },
            lockPosition: 'left',
          };
        }

        if (entities?.['date']) {
          entities['date'] = {
            ...entities['date'],
            headerComponentParams: {
              enableMoving: false,
            },
            lockPosition: 'left',
          };

          if (entities?.['user']) {
            entities['user'] = {
              ...entities['user'],
              headerComponentParams: {
                enableSorting: false,
              },
            };
            delete entities['user']['lockPosition'];
            delete entities['user']['lockPinned'];
          }

          if (entities?.['device']) {
            entities['device'] = {
              ...entities['device'],
              headerComponentParams: {
                enableSorting: false,
              },
            };
          }

          if (entities?.['site']) {
            entities['site'] = {
              ...entities['site'],
              headerComponentParams: {
                enableSorting: false,
              },
            };
          }

          if (entities?.['status']) {
            entities['status'].width = 193;
          }
        }

        if (data?.appData?.table?.[key]?.sort) {
          delete data.appData.table[key].sort;
        }
      }
    });

    data.appData.version = 9;

    return data;
  },
  9: (data: any) => {
    const siteEntities = data?.appData?.table?.['sites']?.columns?.entities;

    if (siteEntities?.['lastUpdated']) {
      siteEntities['lastUpdated'] = {
        ...siteEntities['lastUpdated'],
        sortKey: 'order[controller.status.statisticUpdatedAt]',
        headerComponentParams: {
          enableSorting: true,
        },
      };
    }

    const deviceEntities = data?.appData?.table?.['devices']?.columns?.entities;

    if (deviceEntities?.['lastUpdated']) {
      deviceEntities['lastUpdated'] = {
        ...deviceEntities['lastUpdated'],
        sortKey: 'order[status.statisticUpdatedAt]',
        headerComponentParams: {
          enableSorting: true,
        },
      };
    }

    data.appData.version = 10;

    return data;
  },
  10: (data: any) => {
    const ramDeviceColumn =
      data?.appData?.table?.['devices']?.columns?.entities?.['ram'];

    if (ramDeviceColumn) {
      ramDeviceColumn.width = 180;
      ramDeviceColumn.minWidth = 180;
    }

    data.appData.version = 11;

    return data;
  },
  11: (data: any): any => {
    const clientsStatusColumn =
      data?.appData?.table?.['hosts']?.columns?.entities?.['status'];

    if (clientsStatusColumn) {
      clientsStatusColumn.width = 135;
    }

    const tables = data?.appData?.table;

    Object.keys(tables ?? []).forEach((key) => {
      if (tables?.[key]?.itemsPerPageCount > 100) {
        tables![key].itemsPerPageCount = 100;
      }
    });

    data.appData.version = 12;

    return data;
  },
  12: (data: any) => {
    const siteEntities = data?.appData?.table?.['sites']?.columns?.entities;

    if (siteEntities?.['address']) {
      siteEntities['address'] = {
        ...siteEntities['address'],
        sortKey: 'order[locationAddress]',
        headerComponentParams: {
          enableSorting: true,
        },
      };
    }

    data.appData.version = 13;

    return data;
  },
  13: (data: any) => {
    const migratedData = structuredClone(data);

    if (!migratedData.appData?.locale) {
      migratedData.appData.locale = environment.defaultLocale;
    }

    if (migratedData.appData.version) {
      delete migratedData.appData.version;
    }

    if (migratedData?.appData?.table) {
      Object.values(migratedData.appData.table).forEach((table: any) => {
        table.columns.entities = Object.entries(table.columns.entities).reduce(
          (acc, [key, column]: [string, any]) => ({
            ...acc,
            [key]: {
              hide: column.hide ?? false,
              width: column.width,
              pinned: column.pinned ?? false,
            },
          }),
          {},
        );
      });
    }

    merge(migratedData, {
      user: { appData: { schemaVersion: 14 } },
    });

    return migratedData;
  },
  14: (data: any) => {
    const migratedData = structuredClone(data);

    if (migratedData?.appData?.table) {
      Object.values(migratedData.appData.table).forEach((table: any) => {
        const entities = table?.columns?.entities;
        if (entities?.['status']) {
          entities['status'] = {
            ...entities['status'],
            hide: false,
          };
        }
      });
    }

    migratedData.appData.schemaVersion = 15;
    return migratedData;
  },
  15: (data: any) => {
    const migratedData = structuredClone(data);

    if (
      !environment.global &&
      migratedData.appData.locale !== EnvironmentLocalesEnum.RU
    ) {
      migratedData.appData.locale = EnvironmentLocalesEnum.RU;
    }

    migratedData.appData.schemaVersion = 16;

    return migratedData;
  },
  16: (data: any) => data,
};
